import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Request } from '../../services/requestsService';
import dataService from '../../services/dataService';
import carService from '../../services/carService';
import calibrationService, { CalibrationTable } from '../../services/calibrationService';
import authService from '../../services/authService';
import { ChevronRightIcon, HomeIcon, TaskIcon } from '../icons';
import CalibrationTableModal from '../CalibrationTableModal';
import TaskDetailHeader from './TaskDetailHeader';
import TaskDetailSections from './TaskDetailSections';
import TaskDetailBreadcrumbs from './TaskDetailBreadcrumbs';
import './TaskDetailPage.css';

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Request | null>(null);
  const [carDetails, setCarDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [editingTable, setEditingTable] = useState<CalibrationTable | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Получаем все задачи пользователя и находим нужную
      const userId = authService.getUserId();
      if (!userId) {
        throw new Error('Не удалось определить пользователя');
      }
      
      const tasksResponse = await dataService.getRequestsByResponsible(userId);
      const foundTask = tasksResponse.content.find((t: Request) => t.id === Number(taskId));
      
      if (foundTask) {
        setTask(foundTask);
        
        // Загружаем полную информацию о машине
        if (foundTask.car?.id) {
          try {
            const carData = await carService.getCarById(foundTask.car.id);
            setCarDetails(carData);
            
            // Загружаем тарировочные таблицы для машины
            try {
              const calibrationTables = await calibrationService.getCalibrationTablesByCar(foundTask.car.id);
              setCarDetails((prev: any) => ({
                ...prev,
                calibrationTables: calibrationTables
              }));
            } catch (calibrationError) {
              console.error('Error fetching calibration tables:', calibrationError);
              // Не показываем ошибку, если не удалось загрузить тарировочные таблицы
            }
          } catch (carError) {
            console.error('Error fetching car details:', carError);
            // Не показываем ошибку, если не удалось загрузить детали машины
          }
        }
      } else {
        setError('Задача не найдена');
      }
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError('Ошибка при загрузке деталей задачи');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeInWork = async () => {
    if (!task) return;

    try {
      setActionLoading(true);
      setError(null);
      
      const userId = authService.getUserId();
      if (!userId) {
        throw new Error('Не удалось определить пользователя');
      }
      
      console.log('Taking task in work:', task.id, 'for user:', userId);
      await dataService.takeRequestInWork(task.id, userId);
      
      // Обновляем данные задачи
      fetchTaskDetails();
    } catch (err) {
      console.error('Error taking task in work:', err);
      setError('Ошибка при принятии задачи в работу');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!task) return;

    try {
      setActionLoading(true);
      setError(null);
      
      const userId = authService.getUserId();
      if (!userId) {
        throw new Error('Не удалось определить пользователя');
      }
      
      console.log('Completing task:', task.id, 'for user:', userId);
      await dataService.completeRequest(task.id, userId);
      
      // Обновляем данные задачи
      fetchTaskDetails();
    } catch (err) {
      console.error('Error completing task:', err);
      setError('Ошибка при завершении задачи');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateCalibrationTable = () => {
    setEditingTable(null);
    setIsEditMode(false);
    setShowCalibrationModal(true);
  };

  const handleEditCalibrationTable = (table: CalibrationTable) => {
    setEditingTable(table);
    setIsEditMode(true);
    setShowCalibrationModal(true);
  };

  const handleCalibrationModalClose = () => {
    setShowCalibrationModal(false);
    setEditingTable(null);
    setIsEditMode(false);
  };

  const handleCalibrationModalSuccess = () => {
    // Обновляем данные машины после создания/редактирования таблицы
    if (task?.car?.id) {
      carService.getCarById(task.car.id).then(async (carData) => {
        try {
          const calibrationTables = await calibrationService.getCalibrationTablesByCar(task.car.id);
          setCarDetails({
            ...carData,
            calibrationTables: calibrationTables
          });
        } catch (error) {
          console.error('Error fetching calibration tables:', error);
          setCarDetails(carData);
        }
      }).catch(console.error);
    }
  };

  const handleDeleteCalibrationTable = async (tableId: number, tableName: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить таблицу "${tableName}"?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      
      await calibrationService.deleteCalibrationTable(tableId);
      
      // Обновляем данные машины после удаления таблицы
      if (task?.car?.id) {
        try {
          const carData = await carService.getCarById(task.car.id);
          const calibrationTables = await calibrationService.getCalibrationTablesByCar(task.car.id);
          setCarDetails({
            ...carData,
            calibrationTables: calibrationTables
          });
        } catch (error) {
          console.error('Error updating car data:', error);
        }
      }
    } catch (err) {
      console.error('Error deleting calibration table:', err);
      setError('Ошибка при удалении калибровочной таблицы');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="task-detail-page">
        <TaskDetailBreadcrumbs taskTitle="Загрузка..." />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка деталей задачи...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="task-detail-page">
        <TaskDetailBreadcrumbs taskTitle="Ошибка" />
        <div className="error-container">
          <p className="error-message">{error || 'Задача не найдена'}</p>
          <button onClick={() => navigate('/dashboard')} className="retry-button">
            Вернуться к задачам
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="task-detail-page">
      <TaskDetailBreadcrumbs taskTitle={task.title} />

      <div className="task-detail-content">
        <TaskDetailHeader
          task={task}
          actionLoading={actionLoading}
          onTakeInWork={handleTakeInWork}
          onComplete={handleComplete}
          onBack={() => navigate('/dashboard')}
        />

        <TaskDetailSections
          task={task}
          carDetails={carDetails}
          actionLoading={actionLoading}
        />

        {error && (
          <div className="task-detail-error">
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Модальное окно для калибровочных таблиц */}
      {task?.car?.id && (
        <CalibrationTableModal
          isOpen={showCalibrationModal}
          onClose={handleCalibrationModalClose}
          onSuccess={handleCalibrationModalSuccess}
          carId={task.car.id}
          table={editingTable}
          isEdit={isEditMode}
        />
      )}
    </div>
  );
};

export default TaskDetailPage; 